<script lang="ts">
  import GreekText from "../../ui/GreekText.svelte";

  type Item = {
    sentence: string;
    answer?: string;
    acceptableAnswers?: string[];
    hint?: string;
    english?: string;
  };
  let {
    items,
    blankItems,
    title,
    instructions,
  }: { items?: Item[]; blankItems?: Item[]; title?: string; instructions?: string } = $props();

  let answers = $state<Record<number, string>>({});
  let submitted = $state(false);
  const blanks = $derived(items ?? blankItems ?? []);
  const answeredCount = $derived(
    blanks.filter((_, idx) => (answers[idx] ?? "").trim().length > 0).length
  );
  const correctCount = $derived(
    blanks.filter((item, idx) => isCorrect(item, answers[idx] ?? "")).length
  );

  function normalize(s: string) {
    return s
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim();
  }

  function expectedAnswers(item: Item) {
    return [item.answer, ...(item.acceptableAnswers ?? [])].filter(
      (answer): answer is string => typeof answer === "string" && answer.trim().length > 0,
    );
  }

  function isCorrect(item: Item, userAnswer: string) {
    const normalizedAnswer = normalize(userAnswer);
    return normalizedAnswer.length > 0 && expectedAnswers(item).some((answer) => normalize(answer) === normalizedAnswer);
  }

  function checkAnswers() {
    submitted = true;
  }
</script>

{#if title}
  <h3 class="text-base font-semibold tracking-tight mt-6 mb-2">{title}</h3>
{/if}
{#if instructions}
  <p class="text-sm text-(--color-muted) mb-3">{instructions}</p>
{/if}
<ol class="my-6 space-y-3 list-decimal pl-5">
  {#each blanks as item, idx}
    {@const parts = item.sentence.split("___")}
    {@const userAnswer = answers[idx] ?? ""}
    {@const correct = submitted && isCorrect(item, userAnswer)}
    {@const expected = expectedAnswers(item)}
    <li class="leading-loose">
      <span class="font-serif text-lg">
        {parts[0]}
        <input
          type="text"
          value={userAnswer}
          oninput={(e) => {
            answers = { ...answers, [idx]: (e.target as HTMLInputElement).value };
            submitted = false;
          }}
          class="inline-block min-w-[6rem] px-2 py-0.5 border-b-2 bg-transparent font-serif text-lg
            {submitted
            ? correct
              ? 'border-[#16a34a] text-[#15803d]'
              : 'border-(--color-danger) text-(--color-danger)'
            : 'border-(--color-border-strong) focus:border-(--color-accent)'}
            outline-none"
          placeholder=" "
        />{parts.slice(1).join("___")}
      </span>
      {#if item.hint}
        <span class="block text-xs text-(--color-muted) mt-0.5">Hint: {item.hint}</span>
      {/if}
      {#if item.english}
        <span class="block text-xs text-(--color-muted) mt-0.5">{item.english}</span>
      {/if}
      {#if submitted && !correct && expected.length > 0}
        <span class="block text-xs text-(--color-muted) mt-0.5">
          Answer: <GreekText>{expected[0]}</GreekText>
        </span>
      {:else if submitted && expected.length === 0}
        <span class="block text-xs text-(--color-danger) mt-0.5">
          This item is missing an answer key.
        </span>
      {/if}
    </li>
  {/each}
</ol>

<div class="mt-3 flex flex-wrap items-center gap-3">
  <button
    type="button"
    onclick={checkAnswers}
    disabled={blanks.length === 0}
    class="rounded-md bg-(--color-accent) px-4 py-2 text-sm font-medium text-white hover:bg-(--color-accent-hover) disabled:opacity-50"
  >
    Check answers
  </button>
  {#if submitted}
    <span class="text-sm text-(--color-muted)">
      {correctCount} / {blanks.length} correct{answeredCount < blanks.length ? ` · ${blanks.length - answeredCount} unanswered` : ""}
    </span>
  {/if}
</div>
